/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext, useState, useEffect, useRef } from "react";
import { Stage, Layer, Rect, Circle, Text, Group, Line, Image as KonvaImage, Arc } from "react-konva";
import { contextProvider } from "../../../context/ContextProvider";
import Alerts from "../../components/Alerts/Alerts";
import RestPrivateAntesala from "../../../helpers/RestPrivateApi";

const GRID_SIZE = 28;
const CANVAS_WIDTH = 1600;
const CANVAS_HEIGHT = 1000;

// Componente para el escenario
const Escenario = ({ escenario, isSelected, onSelect, onDragEnd }) => {
  const shapeRef = useRef();
  const [isDragging, setIsDragging] = useState(false);

  const handleDragMove = (e) => {
    // Restringir movimiento dentro del área de trabajo (0 a 5000)
    const x = Math.max(0, Math.min(5000 - escenario.width, e.target.x()));
    const y = Math.max(0, Math.min(5000 - escenario.height, e.target.y()));
    e.target.x(x);
    e.target.y(y);
  };

  return (
    <Group
      x={escenario.x}
      y={escenario.y}
      draggable
      onClick={onSelect}
      onTap={onSelect}
      onDragStart={() => setIsDragging(true)}
      onDragMove={handleDragMove}
      onDragEnd={(e) => {
        setIsDragging(false);
        onDragEnd({
          ...escenario,
          x: e.target.x(),
          y: e.target.y(),
        });
      }}
      onTransformEnd={() => {
        const node = shapeRef.current;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();

        node.scaleX(1);
        node.scaleY(1);
        onDragEnd({
          ...escenario,
          x: node.x(),
          y: node.y(),
          width: Math.max(5, node.width() * scaleX),
          height: Math.max(node.height() * scaleY),
        });
      }}
      opacity={isDragging ? 0.7 : 1}
      onMouseEnter={(e) => {
        const container = e.target.getStage().container();
        container.style.cursor = 'move';
      }}
      onMouseLeave={(e) => {
        const container = e.target.getStage().container();
        container.style.cursor = 'default';
      }}
    >
      {escenario.tipo === "semicircular" ? (
        <>
          <Arc
            ref={shapeRef}
            width={escenario.width}
            height={escenario.height}
            angle={180}
            rotation={180}
            fill="#2C3E50"
            stroke="#34495E"
            strokeWidth={3}
            shadowColor={isDragging ? "rgba(0, 0, 0, 0.5)" : isSelected ? "rgba(52, 152, 219, 0.5)" : "transparent"}
            shadowBlur={isDragging ? 20 : 10}
            shadowOffsetX={isDragging ? 10 : 0}
            shadowOffsetY={isDragging ? 10 : 0}
          />
          <Text
            text="ESCENARIO"
            fontSize={16}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
            width={escenario.width}
            x={0}
            y={escenario.height / 2 - 8}
          />
        </>
      ) : (
        <>
          <Rect
            ref={shapeRef}
            width={escenario.width}
            height={escenario.height}
            fill="#2C3E50"
            stroke="#34495E"
            strokeWidth={3}
            shadowColor={isDragging ? "rgba(0, 0, 0, 0.5)" : isSelected ? "rgba(52, 152, 219, 0.5)" : "transparent"}
            shadowBlur={isDragging ? 20 : 10}
            shadowOffsetX={isDragging ? 10 : 0}
            shadowOffsetY={isDragging ? 10 : 0}
          />
          <Text
            text="ESCENARIO"
            fontSize={16}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
            width={escenario.width}
            height={escenario.height}
          />
        </>
      )}
      {isSelected && (
        <Rect
          x={-5}
          y={-5}
          width={escenario.width + 10}
          height={escenario.height + 10}
          stroke="#3498db"
          strokeWidth={3}
          dash={[5, 5]}
        />
      )}
    </Group>
  );
};

// Componente para un asiento
const Asiento = ({ asiento, sector, isSelected, isMultiSelected, onSelect, onDragEnd, onDragStart, herramientaActiva, onDoubleClick }) => {
  const [isDragging, setIsDragging] = useState(false);
  const shapeRef = useRef();

  if (!sector || !sector.visible) return null;

  let color = sector.color || "#28a745";
  if (asiento.estado === "reservado") color = "#FFA500";
  if (asiento.estado === "vendido") color = "#DC3545";
  if (asiento.estado === "bloqueado") color = "#6C757D";

  // Convertir color hex a rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handleDragMove = (e) => {
    // Restringir movimiento dentro del área de trabajo
    let x = Math.max(0, Math.min(5000 - asiento.width, e.target.x()));
    let y = Math.max(0, Math.min(5000 - asiento.height, e.target.y()));

    // Snap a cuadrícula en tiempo real para mejor feedback visual
    const snapX = Math.round(x / GRID_SIZE) * GRID_SIZE;
    const snapY = Math.round(y / GRID_SIZE) * GRID_SIZE;

    e.target.x(snapX);
    e.target.y(snapY);
  };

  // Efecto para aplicar transformaciones visuales durante el arrastre
  useEffect(() => {
    if (shapeRef.current) {
      shapeRef.current.to({
        scaleX: isDragging ? 1.1 : 1,
        scaleY: isDragging ? 1.1 : 1,
        duration: 0.1,
      });
    }
  }, [isDragging]);

  return (
    <Group
      x={asiento.x}
      y={asiento.y}
      draggable
      onClick={onSelect}
      onTap={onSelect}
      onDblClick={() => onDoubleClick && onDoubleClick(asiento)}
      onDblTap={() => onDoubleClick && onDoubleClick(asiento)}
      onDragStart={(e) => {
        setIsDragging(true);
        if (onDragStart) {
          onDragStart(asiento, e);
        }
      }}
      onDragMove={handleDragMove}
      onDragEnd={(e) => {
        setIsDragging(false);
        // Snap to grid
        const newX = Math.round(e.target.x() / GRID_SIZE) * GRID_SIZE;
        const newY = Math.round(e.target.y() / GRID_SIZE) * GRID_SIZE;
        onDragEnd({
          ...asiento,
          x: newX,
          y: newY,
        }, asiento);
      }}
      onMouseEnter={(e) => {
        const container = e.target.getStage().container();
        if (herramientaActiva === "eliminar_asiento") {
          container.style.cursor = 'pointer';
        } else {
          container.style.cursor = 'grab';
        }
      }}
      onMouseLeave={(e) => {
        const container = e.target.getStage().container();
        if (herramientaActiva === "eliminar_asiento") {
          container.style.cursor = 'not-allowed';
        } else {
          container.style.cursor = 'default';
        }
      }}
      onMouseDown={(e) => {
        const container = e.target.getStage().container();
        if (herramientaActiva !== "eliminar_asiento") {
          container.style.cursor = 'grabbing';
        }
      }}
      onMouseUp={(e) => {
        const container = e.target.getStage().container();
        if (herramientaActiva === "eliminar_asiento") {
          container.style.cursor = 'pointer';
        } else {
          container.style.cursor = 'grab';
        }
      }}
    >
      <Rect
        ref={shapeRef}
        width={asiento.width}
        height={asiento.height}
        fill={hexToRgba(color, 0.3)}
        stroke={color}
        strokeWidth={2}
        cornerRadius={2}
        shadowColor={isDragging ? "rgba(0, 0, 0, 0.3)" : isSelected || isMultiSelected ? "rgba(52, 152, 219, 0.5)" : "transparent"}
        shadowBlur={isDragging ? 20 : isSelected || isMultiSelected ? 10 : 0}
        shadowOffsetX={isDragging ? 8 : 0}
        shadowOffsetY={isDragging ? 8 : 0}
      />
      <Text
        text={asiento.numero}
        fontSize={10}
        fontStyle="bold"
        fill={color}
        align="center"
        verticalAlign="middle"
        width={asiento.width}
        height={asiento.height}
      />
      {isSelected && (
        <Rect
          x={-3}
          y={-3}
          width={asiento.width + 6}
          height={asiento.height + 6}
          stroke="#3498db"
          strokeWidth={3}
          dash={[3, 3]}
        />
      )}
      {isMultiSelected && !isSelected && (
        <Rect
          x={-3}
          y={-3}
          width={asiento.width + 6}
          height={asiento.height + 6}
          stroke="#28a745"
          strokeWidth={2}
          dash={[5, 5]}
        />
      )}
      {/* Indicador visual en modo eliminar */}
      {herramientaActiva === "eliminar_asiento" && (
        <Text
          text="✖"
          fontSize={14}
          fontStyle="bold"
          x={asiento.width / 2 - 5}
          y={-18}
          fill="#DC3545"
        />
      )}
    </Group>
  );
};

// Componente para elementos especiales
const ElementoEspecial = ({ elemento, isSelected, onSelect, onDragEnd }) => {
  const [isDragging, setIsDragging] = useState(false);

  let color = "#666666";
  switch (elemento.tipo) {
    case "escalera":
      color = "#95A5A6";
      break;
    case "ingreso":
      color = "#27AE60";
      break;
    case "salida_emergencia":
      color = "#E74C3C";
      break;
    case "baño":
      color = "#3498DB";
      break;
    case "pasillo":
      color = "#BDC3C7";
      break;
    default:
      color = "#7F8C8D";
  }

  const texto = elemento.etiqueta || elemento.tipo.replace('_', ' ');

  const handleDragMove = (e) => {
    // Restringir movimiento dentro del área de trabajo
    const x = Math.max(0, Math.min(5000 - elemento.width, e.target.x()));
    const y = Math.max(0, Math.min(5000 - elemento.height, e.target.y()));
    e.target.x(x);
    e.target.y(y);
  };

  return (
    <Group
      x={elemento.x}
      y={elemento.y}
      draggable
      onClick={onSelect}
      onTap={onSelect}
      onDragStart={() => setIsDragging(true)}
      onDragMove={handleDragMove}
      onDragEnd={(e) => {
        setIsDragging(false);
        const newX = Math.round(e.target.x() / GRID_SIZE) * GRID_SIZE;
        const newY = Math.round(e.target.y() / GRID_SIZE) * GRID_SIZE;
        onDragEnd({
          ...elemento,
          x: newX,
          y: newY,
        });
      }}
      opacity={isDragging ? 0.7 : 1}
      onMouseEnter={(e) => {
        const container = e.target.getStage().container();
        container.style.cursor = 'grab';
      }}
      onMouseLeave={(e) => {
        const container = e.target.getStage().container();
        container.style.cursor = 'default';
      }}
      onMouseDown={(e) => {
        const container = e.target.getStage().container();
        container.style.cursor = 'grabbing';
      }}
      onMouseUp={(e) => {
        const container = e.target.getStage().container();
        container.style.cursor = 'grab';
      }}
    >
      <Rect
        width={elemento.width}
        height={elemento.height}
        fill={color}
        stroke="#34495E"
        strokeWidth={2}
        shadowColor={isDragging ? "rgba(0, 0, 0, 0.5)" : isSelected ? "rgba(52, 152, 219, 0.5)" : "transparent"}
        shadowBlur={isDragging ? 15 : 10}
        shadowOffsetX={isDragging ? 5 : 0}
        shadowOffsetY={isDragging ? 5 : 0}
      />
      <Text
        text={texto.toUpperCase()}
        fontSize={Math.min(elemento.height * 0.4, 16)}
        fontStyle="bold"
        fill="#FFFFFF"
        align="center"
        verticalAlign="middle"
        width={elemento.width}
        height={elemento.height}
      />
      {isSelected && (
        <Rect
          x={-5}
          y={-5}
          width={elemento.width + 10}
          height={elemento.height + 10}
          stroke="#3498db"
          strokeWidth={3}
          dash={[5, 5]}
        />
      )}
    </Group>
  );
};

// Componente para la imagen de fondo
const BackgroundImage = ({ imagenFondo, opacidad, width, height }) => {
  const [image, setImage] = useState(null);

  useEffect(() => {
    if (imagenFondo) {
      const img = new window.Image();
      img.onload = () => {
        // Validar que la imagen tenga dimensiones válidas
        if (img.width > 0 && img.height > 0) {
          setImage(img);
        } else {
          console.error("Error: La imagen cargada no tiene dimensiones válidas");
          setImage(null);
        }
      };
      img.onerror = () => {
        console.error("Error al cargar la imagen de fondo");
        setImage(null);
      };
      img.src = imagenFondo;
    } else {
      setImage(null);
    }
  }, [imagenFondo]);

  // Solo renderizar si la imagen está cargada y las dimensiones son válidas
  if (!image || !width || !height || width === 0 || height === 0) {
    return null;
  }

  return (
    <KonvaImage
      image={image}
      width={width}
      height={height}
      opacity={opacidad}
    />
  );
};

// Componente principal
const MapaTeatroKonva = () => {
  const context = useContext(contextProvider);
  const stageRef = useRef();

  // Estados para sala y carga
  const [salas, setSalas] = useState([]);
  const [salaSeleccionada, setSalaSeleccionada] = useState(null);
  const [loading, setLoading] = useState(false);
  const [siteMapId, setSiteMapId] = useState(null);
  const [mapasDisponibles, setMapasDisponibles] = useState([]);
  const [mostrarSeleccionMapa, setMostrarSeleccionMapa] = useState(false);
  const [mapaSeleccionadoVista, setMapaSeleccionadoVista] = useState(null);
  const [nombreMapa, setNombreMapa] = useState("");

  // Estados del editor
  const [herramientaActiva, setHerramientaActiva] = useState("seleccionar");
  const [zoom, setZoom] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [sectorActivoPintar, setSectorActivoPintar] = useState(null); // Sector seleccionado para pintar asientos

  // Estados del mapa
  const [sectores, setSectores] = useState([]);
  const [asientos, setAsientos] = useState([]);
  const [escenario, setEscenario] = useState({
    x: 400,
    y: 50,
    width: 300,
    height: 60,
    tipo: "semicircular"
  });
  const [elementosEspeciales, setElementosEspeciales] = useState([]);

  // Estados para imagen de fondo
  const [imagenFondo, setImagenFondo] = useState(null);
  const [opacidadFondo, setOpacidadFondo] = useState(0.3);

  // Estados para configuración del área de trabajo
  const [workspaceGridColumns, setWorkspaceGridColumns] = useState(57); // 57 * 28 = 1596px ≈ 1600px
  const [workspaceGridRows, setWorkspaceGridRows] = useState(36);       // 36 * 28 = 1008px ≈ 1000px
  const [workspaceWidth, setWorkspaceWidth] = useState(CANVAS_WIDTH);
  const [workspaceHeight, setWorkspaceHeight] = useState(CANVAS_HEIGHT);
  const [showWorkspaceConfig, setShowWorkspaceConfig] = useState(false);

  // Actualizar dimensiones del workspace cuando cambian las cuadrículas
  useEffect(() => {
    setWorkspaceWidth(workspaceGridColumns * GRID_SIZE);
    setWorkspaceHeight(workspaceGridRows * GRID_SIZE);
  }, [workspaceGridColumns, workspaceGridRows]);

  // Estados de selección
  const [elementoSeleccionado, setElementoSeleccionado] = useState(null);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [asientosSeleccionados, setAsientosSeleccionados] = useState([]); // IDs de asientos seleccionados

  // Estados para selección por rectángulo
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [selectionStart, setSelectionStart] = useState({ x: 0, y: 0 });

  // Estados para gestión de sectores
  const [mostrarModalSector, setMostrarModalSector] = useState(false);
  const [sectorEditando, setSectorEditando] = useState(null);
  const [formSector, setFormSector] = useState({
    nombre: "",
    sector: "",
    tipo: "",
    gratis: false,
    precio: 0,
    stock: 0,
    multiplicador: 1,
    color: "#28a745",
    habilitacion_fecha: "",
    habilitacion_hora: "",
    vencimiento_fecha: "",
    vencimiento_hora: ""
  });

  // Estados para elementos especiales
  const [mostrarModalElementoEspecial, setMostrarModalElementoEspecial] = useState(false);
  const [elementoEspecialEditando, setElementoEspecialEditando] = useState(null);
  const [formElementoEspecial, setFormElementoEspecial] = useState({
    tipo: "escalera",
    etiqueta: "",
    width: 100,
    height: 50,
    x: 100,
    y: 100
  });

  // Estados para configuración del escenario
  const [mostrarModalEscenario, setMostrarModalEscenario] = useState(false);

  // Estados para configuración del generador de asientos
  const [mostrarModalGenerador, setMostrarModalGenerador] = useState(false);
  const [sectorGenerando, setSectorGenerando] = useState(null);
  const [configGenerador, setConfigGenerador] = useState({
    filas: 10,
    asientosPorFila: 15,
    espacioHorizontal: 56, // 2 * GRID_SIZE para alineación perfecta
    espacioVertical: 56, // 2 * GRID_SIZE para alineación perfecta
    offsetX: 112, // 4 * GRID_SIZE
    offsetY: 224, // 8 * GRID_SIZE
    forma: "cuadrado" // cuadrado, circular, semicircular
  });

  // Estados para organizar asientos seleccionados
  const [mostrarModalOrganizar, setMostrarModalOrganizar] = useState(false);
  const [configOrganizar, setConfigOrganizar] = useState({
    forma: "linea", // linea, arco, circulo, grid
    espaciado: 35,
    radio: 200,
    angulo: 180
  });

  // Estados para editar entrada/asiento
  const [mostrarModalEditarEntrada, setMostrarModalEditarEntrada] = useState(false);
  const [asientoEditando, setAsientoEditando] = useState(null);
  const [formEntrada, setFormEntrada] = useState({
    nombre: "",
    sector: "",
    tipo: "Venta",
    precio: 0,
    habilitacion_fecha: "",
    habilitacion_hora: "",
    vencimiento_fecha: "",
    vencimiento_hora: "",
    stock: 1
  });

  // Cargar lista de salas al montar el componente
  useEffect(() => {
    cargarSalas();
  }, []);

  const cargarSalas = async () => {
    const response = await RestPrivateAntesala({
      url: "/salas",
      method: "get",
      setLoading: setLoading,
    });

    if (response.status === 200) {
      setSalas(response.data.data || []);
    }
  };

  // Cargar mapas disponibles cuando se selecciona una sala
  const cargarMapasDisponibles = async (salaId) => {
    if (!salaId) return;

    const response = await RestPrivateAntesala({
      url: `/site-maps?sala_id=${salaId}`,
      method: "get",
      setLoading: setLoading,
    });

    if (response.status === 200 && response.data.data) {
      const mapas = Array.isArray(response.data.data) ? response.data.data : [];
      setMapasDisponibles(mapas);

      if (mapas.length > 0) {
        setMostrarSeleccionMapa(true);
      } else {
        // No hay mapas, crear uno nuevo automáticamente
        crearMapaNuevo();
      }
    } else {
      // Error o no hay mapas, crear uno nuevo
      setMapasDisponibles([]);
      crearMapaNuevo();
    }
  };

  // Cargar un mapa específico
  const cargarMapaEspecifico = (siteMapData) => {
    setSiteMapId(siteMapData.id);

    let parsedSiteMap = siteMapData.site_map;
    if (typeof parsedSiteMap === 'string') {
      try {
        parsedSiteMap = JSON.parse(parsedSiteMap);
      } catch (e) {
        console.error("Error parseando site_map:", e);
        return;
      }
    }

    if (parsedSiteMap) {
      setAsientos(parsedSiteMap.asientos || []);
      setEscenario(parsedSiteMap.escenario || {
        x: 400,
        y: 50,
        width: 300,
        height: 60,
        tipo: "semicircular"
      });
      setElementosEspeciales(parsedSiteMap.elementosEspeciales || []);
      setImagenFondo(parsedSiteMap.imagenFondo || null);
      setOpacidadFondo(parsedSiteMap.opacidadFondo || 0.3);
      setSectores(parsedSiteMap.sectores || []);
      setNombreMapa(parsedSiteMap.nombre || siteMapData.nombre || "");

      // Cargar configuración del workspace (tamaño del área de trabajo)
      if (parsedSiteMap.workspaceGridColumns && parsedSiteMap.workspaceGridRows) {
        setWorkspaceGridColumns(parsedSiteMap.workspaceGridColumns);
        setWorkspaceGridRows(parsedSiteMap.workspaceGridRows);
      } else {
        // Valores por defecto si no están definidos
        setWorkspaceGridColumns(57);
        setWorkspaceGridRows(36);
      }
    }

    setMostrarSeleccionMapa(false);
    setMapaSeleccionadoVista(siteMapData);
  };

  // Crear un mapa nuevo vacío
  const crearMapaNuevo = () => {
    setSiteMapId(null);
    setAsientos([]);
    setSectores([]);
    setElementosEspeciales([]);
    setEscenario({
      x: 400,
      y: 50,
      width: 300,
      height: 60,
      tipo: "semicircular"
    });
    setImagenFondo(null);
    setOpacidadFondo(0.3);
    setNombreMapa("");
    setMostrarSeleccionMapa(false);
    setMapaSeleccionadoVista(null);
  };

  useEffect(() => {
    if (salaSeleccionada) {
      cargarMapasDisponibles(salaSeleccionada);
    } else {
      setMapasDisponibles([]);
      setMostrarSeleccionMapa(false);
      setMapaSeleccionadoVista(null);
    }
  }, [salaSeleccionada]);

  // Guardar mapa
  const guardarMapa = async () => {
    if (!salaSeleccionada) {
      Alerts({
        msg: "Error",
        title: "Debes seleccionar una sala primero",
        code: "400",
        type: "error",
      });
      return;
    }

    const siteMapData = {
      version: "2.0",
      nombre: nombreMapa || "Mapa sin nombre",
      asientos: asientos,
      escenario: escenario,
      elementosEspeciales: elementosEspeciales,
      imagenFondo: imagenFondo,
      opacidadFondo: opacidadFondo,
      // Guardar configuración del workspace (tamaño del área de trabajo)
      workspaceGridColumns: workspaceGridColumns,
      workspaceGridRows: workspaceGridRows,
      sectores: sectores.map(s => ({
        id: s.id,
        nombre: s.nombre,
        sector: s.sector,
        habilitacion_fecha: s.habilitacion_fecha,
        habilitacion_hora: s.habilitacion_hora,
        vencimiento_fecha: s.vencimiento_fecha,
        vencimiento_hora: s.vencimiento_hora,
        tipo: s.tipo || "",
        gratis: s.gratis || false,
        precio: s.precio || 0,
        stock: s.capacidad || s.stock || 0,
        multiplicador: null,
        color: s.color,
        visible: s.visible !== undefined ? s.visible : true
      }))
    };


    const formData = new FormData();
    formData.append('sala_id', salaSeleccionada)
    formData.append('nombre', nombreMapa || "Mapa sin nombre")
    formData.append('site_map', JSON.stringify(siteMapData))

    if (siteMapId) {
      formData.append('_method', 'PUT')
    }

    const method = siteMapId ? "POST" : "POST";
    const url = siteMapId ? `/site-maps/${siteMapId}` : "/site-maps";



    const response = await RestPrivateAntesala({
      url: url,
      method: method,
      formData: formData,
      setLoading: setLoading,
    });

    if (response.status === 200 || response.status === 201) {
      Alerts({
        msg: "Éxito",
        title: "Mapa guardado correctamente",
        code: "200",
        type: "success",
      });
      if (!siteMapId && response.data.data) {
        setSiteMapId(response.data.data.id);
      }
    } else {
      Alerts({
        msg: "Error",
        title: "Error al guardar el mapa",
        code: "500",
        type: "error",
      });
    }
  };

  // Handle zoom
  const handleWheel = (e) => {
    e.evt.preventDefault();

    const scaleBy = 1.1;
    const stage = stageRef.current;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;

    setZoom(newScale);
    setStagePos({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  };

  // Función para agregar asiento individual al hacer click
  const agregarAsientoEnPosicion = (x, y) => {
    if (!sectorActivoPintar) {
      Alerts({
        msg: "Error",
        title: "Debes seleccionar un sector primero",
        code: "400",
        type: "error",
      });
      return;
    }

    const sector = sectores.find(s => s.id === sectorActivoPintar);
    if (!sector) return;

    const stockDisponible = sector.capacidad || sector.stock || 0;

    // Usar setState funcional para validar con el estado más reciente
    setAsientos(prevAsientos => {
      // Calcular con el estado actualizado
      const asientosDelSector = prevAsientos.filter(a => a.sector_id === sectorActivoPintar);

      // Validar que no se exceda el stock del sector
      if (asientosDelSector.length >= stockDisponible) {
        Alerts({
          msg: "Límite alcanzado",
          title: `El sector "${sector.nombre}" ya tiene ${asientosDelSector.length} asientos (Stock máximo: ${stockDisponible})`,
          code: "400",
          type: "warning",
        });
        return prevAsientos; // No agregar, retornar estado anterior
      }

      const siguienteNumero = asientosDelSector.length + 1;

      // Alinear a la cuadrícula
      const alignedX = Math.round(x / GRID_SIZE) * GRID_SIZE;
      const alignedY = Math.round(y / GRID_SIZE) * GRID_SIZE;

      const nuevoAsiento = {
        id: `${sectorActivoPintar}-${Date.now()}-${Math.random()}`, // Agregar random para evitar IDs duplicados
        sector_id: sectorActivoPintar,
        numero: siguienteNumero,
        fila: String.fromCharCode(65 + Math.floor((siguienteNumero - 1) / 10)), // Auto-asignar fila A, B, C...
        columna: ((siguienteNumero - 1) % 10) + 1, // Auto-asignar columna 1-10
        x: alignedX,
        y: alignedY,
        width: 28,
        height: 28,
        estado: "disponible",
        tipo: "normal",
        // Nuevos campos de entrada - heredados del sector
        nombre: `${sector.nombre} - Asiento ${siguienteNumero}`,
        sector: sector.sector || sector.nombre,
        tipo_entrada: sector.precio > 0 ? "Venta" : "Gratis",
        precio: sector.precio || 0,
        habilitacion_fecha: sector.habilitacion_fecha || "",
        habilitacion_hora: sector.habilitacion_hora || "",
        vencimiento_fecha: sector.vencimiento_fecha || "",
        vencimiento_hora: sector.vencimiento_hora || "",
        stock: 1
      };

      return [...prevAsientos, nuevoAsiento];
    });
  };

  // Función para eliminar asiento al hacer click
  const eliminarAsientoEnClick = (asientoId) => {
    setAsientos(asientos.filter(a => a.id !== asientoId));
    // También remover de selección si estaba seleccionado
    setAsientosSeleccionados(asientosSeleccionados.filter(id => id !== asientoId));
    if (elementoSeleccionado?.id === asientoId) {
      setElementoSeleccionado(null);
      setTipoSeleccionado(null);
    }
  };

  // Función para abrir modal de edición de entrada
  const abrirModalEditarEntrada = (asiento) => {
    setAsientoEditando(asiento);
    const sector = sectores.find(s => s.id === asiento.sector_id);
    setFormEntrada({
      nombre: asiento.nombre || `${sector?.nombre || 'Sector'} - Asiento ${asiento.numero}`,
      sector: asiento.sector || sector?.nombre || "",
      tipo: asiento.precio > 0 ? "Venta" : "Gratis",
      precio: asiento.precio || 0,
      habilitacion_fecha: asiento.habilitacion_fecha || "",
      habilitacion_hora: asiento.habilitacion_hora || "",
      vencimiento_fecha: asiento.vencimiento_fecha || "",
      vencimiento_hora: asiento.vencimiento_hora || "",
      stock: asiento.stock || 1
    });
    setMostrarModalEditarEntrada(true);
  };

  // Función para guardar cambios de entrada
  const guardarCambiosEntrada = () => {
    if (!asientoEditando) return;

    // Determinar tipo automáticamente según precio
    const tipoAutomatico = formEntrada.precio > 0 ? "Venta" : "Gratis";

    const asientoActualizado = {
      ...asientoEditando,
      nombre: formEntrada.nombre,
      sector: formEntrada.sector,
      tipo_entrada: tipoAutomatico,
      precio: parseFloat(formEntrada.precio),
      habilitacion_fecha: formEntrada.habilitacion_fecha,
      habilitacion_hora: formEntrada.habilitacion_hora,
      vencimiento_fecha: formEntrada.vencimiento_fecha,
      vencimiento_hora: formEntrada.vencimiento_hora,
      stock: parseInt(formEntrada.stock)
    };

    setAsientos(asientos.map(a => a.id === asientoEditando.id ? asientoActualizado : a));
    setMostrarModalEditarEntrada(false);
    setAsientoEditando(null);
  };

  // Manejar inicio de selección por rectángulo
  const handleStageMouseDown = (e) => {
    const pos = e.target.getStage().getPointerPosition();
    const x = (pos.x - stagePos.x) / zoom;
    const y = (pos.y - stagePos.y) / zoom;

    // Si está en modo "agregar_asiento", crear asiento en cualquier click (excepto en elementos existentes)
    if (herramientaActiva === "agregar_asiento") {
      // Solo crear si NO se clickeó en un asiento, elemento especial o escenario
      const clickedOnElement = e.target.hasName && (
        e.target.parent?.attrs?.draggable === true ||
        e.target.attrs?.draggable === true
      );

      if (!clickedOnElement) {
        agregarAsientoEnPosicion(x, y);
      }
      return;
    }

    // Solo si es click en el stage (fondo) para otras herramientas
    if (e.target === e.target.getStage() || e.target.attrs?.fill === "#FFFFFF") {
      const isCtrlPressed = e.evt?.ctrlKey || e.evt?.metaKey || e.evt?.shiftKey;

      // Si presiona Ctrl/Shift, activar selección por rectángulo
      if (isCtrlPressed || herramientaActiva === "seleccionar") {
        setIsSelecting(true);
        setSelectionStart({ x, y });
        setSelectionRect({ x, y, width: 0, height: 0 });
      } else {
        // Click en el fondo sin Ctrl - deseleccionar todo
        deseleccionarTodos();
      }
    }
  };

  // Manejar movimiento durante selección por rectángulo
  const handleStageMouseMove = (e) => {
    if (!isSelecting) return;

    const pos = e.target.getStage().getPointerPosition();
    const x = (pos.x - stagePos.x) / zoom;
    const y = (pos.y - stagePos.y) / zoom;

    setSelectionRect({
      x: Math.min(selectionStart.x, x),
      y: Math.min(selectionStart.y, y),
      width: Math.abs(x - selectionStart.x),
      height: Math.abs(y - selectionStart.y),
    });
  };

  // Manejar fin de selección por rectángulo
  const handleStageMouseUp = (e) => {
    if (!isSelecting) return;

    setIsSelecting(false);

    // Seleccionar asientos dentro del rectángulo
    if (selectionRect.width > 5 && selectionRect.height > 5) {
      const asientosEnRectangulo = asientos.filter(asiento => {
        return (
          asiento.x >= selectionRect.x &&
          asiento.x + asiento.width <= selectionRect.x + selectionRect.width &&
          asiento.y >= selectionRect.y &&
          asiento.y + asiento.height <= selectionRect.y + selectionRect.height
        );
      });

      const isCtrlPressed = e.evt?.ctrlKey || e.evt?.metaKey || e.evt?.shiftKey;

      if (isCtrlPressed) {
        // Agregar a selección existente
        const nuevosIds = asientosEnRectangulo.map(a => a.id);
        const combinados = [...new Set([...asientosSeleccionados, ...nuevosIds])];
        setAsientosSeleccionados(combinados);
      } else {
        // Reemplazar selección
        setAsientosSeleccionados(asientosEnRectangulo.map(a => a.id));
      }
    }

    setSelectionRect({ x: 0, y: 0, width: 0, height: 0 });
  };

  // Generar asientos para un sector con configuración avanzada
  const generarAsientosParaSector = (sectorId) => {
    setSectorGenerando(sectorId);
    setMostrarModalGenerador(true);
  };

  // Generar asientos según configuración
  const generarAsientosConConfig = () => {
    const sector = sectores.find(s => s.id === sectorGenerando);
    if (!sector) return;

    const stock = sector.capacidad || sector.stock || 100;
    const { filas, asientosPorFila, espacioHorizontal, espacioVertical, offsetX, offsetY, forma } = configGenerador;

    // Asegurar que el offset inicial esté alineado a la cuadrícula
    const alignedOffsetX = Math.round(offsetX / GRID_SIZE) * GRID_SIZE;
    const alignedOffsetY = Math.round(offsetY / GRID_SIZE) * GRID_SIZE;

    const nuevosAsientos = [];
    let numeroAsiento = 1;

    if (forma === "cuadrado") {
      // Disposición en cuadrícula - alineada perfectamente
      for (let fila = 0; fila < filas && numeroAsiento <= stock; fila++) {
        for (let col = 0; col < asientosPorFila && numeroAsiento <= stock; col++) {
          const xPos = alignedOffsetX + col * espacioHorizontal;
          const yPos = alignedOffsetY + fila * espacioVertical;

          nuevosAsientos.push({
            id: `${sectorGenerando}-${numeroAsiento}`,
            sector_id: sectorGenerando,
            numero: numeroAsiento,
            fila: String.fromCharCode(65 + fila),
            columna: col + 1,
            x: Math.round(xPos / GRID_SIZE) * GRID_SIZE,
            y: Math.round(yPos / GRID_SIZE) * GRID_SIZE,
            width: 28,
            height: 28,
            estado: "disponible",
            tipo: "normal",
            // Campos heredados del sector
            nombre: `${sector.nombre} - Asiento ${numeroAsiento}`,
            sector: sector.sector || sector.nombre,
            tipo_entrada: sector.precio > 0 ? "Venta" : "Gratis",
            precio: sector.precio || 0,
            habilitacion_fecha: sector.habilitacion_fecha || "",
            habilitacion_hora: sector.habilitacion_hora || "",
            vencimiento_fecha: sector.vencimiento_fecha || "",
            vencimiento_hora: sector.vencimiento_hora || "",
            stock: 1
          });
          numeroAsiento++;
        }
      }
    } else if (forma === "semicircular") {
      // Disposición semicircular
      const radio = asientosPorFila * espacioHorizontal / Math.PI;
      for (let fila = 0; fila < filas && numeroAsiento <= stock; fila++) {
        const radioActual = radio + fila * espacioVertical;
        for (let col = 0; col < asientosPorFila && numeroAsiento <= stock; col++) {
          const angulo = Math.PI * col / (asientosPorFila - 1);
          const x = alignedOffsetX + radioActual * Math.cos(angulo);
          const y = alignedOffsetY - radioActual * Math.sin(angulo);

          nuevosAsientos.push({
            id: `${sectorGenerando}-${numeroAsiento}`,
            sector_id: sectorGenerando,
            numero: numeroAsiento,
            fila: String.fromCharCode(65 + fila),
            columna: col + 1,
            x: Math.round(x / GRID_SIZE) * GRID_SIZE,
            y: Math.round(y / GRID_SIZE) * GRID_SIZE,
            width: 28,
            height: 28,
            estado: "disponible",
            tipo: "normal",
            // Campos heredados del sector
            nombre: `${sector.nombre} - Asiento ${numeroAsiento}`,
            sector: sector.sector || sector.nombre,
            tipo_entrada: sector.precio > 0 ? "Venta" : "Gratis",
            precio: sector.precio || 0,
            habilitacion_fecha: sector.habilitacion_fecha || "",
            habilitacion_hora: sector.habilitacion_hora || "",
            vencimiento_fecha: sector.vencimiento_fecha || "",
            vencimiento_hora: sector.vencimiento_hora || "",
            stock: 1
          });
          numeroAsiento++;
        }
      }
    } else if (forma === "circular") {
      // Disposición circular
      const radio = asientosPorFila * espacioHorizontal / (2 * Math.PI);
      for (let fila = 0; fila < filas && numeroAsiento <= stock; fila++) {
        const radioActual = radio + fila * espacioVertical;
        const asientosEnFila = Math.floor(2 * Math.PI * radioActual / espacioHorizontal);

        for (let col = 0; col < asientosEnFila && numeroAsiento <= stock; col++) {
          const angulo = (2 * Math.PI * col) / asientosEnFila;
          const x = alignedOffsetX + radioActual * Math.cos(angulo);
          const y = alignedOffsetY + radioActual * Math.sin(angulo);

          nuevosAsientos.push({
            id: `${sectorGenerando}-${numeroAsiento}`,
            sector_id: sectorGenerando,
            numero: numeroAsiento,
            fila: String.fromCharCode(65 + fila),
            columna: col + 1,
            x: Math.round(x / GRID_SIZE) * GRID_SIZE,
            y: Math.round(y / GRID_SIZE) * GRID_SIZE,
            width: 28,
            height: 28,
            estado: "disponible",
            tipo: "normal",
            // Campos heredados del sector
            nombre: `${sector.nombre} - Asiento ${numeroAsiento}`,
            sector: sector.sector || sector.nombre,
            tipo_entrada: sector.precio > 0 ? "Venta" : "Gratis",
            precio: sector.precio || 0,
            habilitacion_fecha: sector.habilitacion_fecha || "",
            habilitacion_hora: sector.habilitacion_hora || "",
            vencimiento_fecha: sector.vencimiento_fecha || "",
            vencimiento_hora: sector.vencimiento_hora || "",
            stock: 1
          });
          numeroAsiento++;
        }
      }
    }

    setAsientos([...asientos, ...nuevosAsientos]);
    setMostrarModalGenerador(false);
    setSectorGenerando(null);
  };

  // Estado para rastrear el asiento que se está arrastrando y posiciones iniciales
  const [dragStartPositions, setDragStartPositions] = useState(new Map());

  // Manejar inicio de arrastre de asiento
  const handleAsientoDragStart = (asiento, e) => {
    // Si el asiento arrastrado está en la selección múltiple, guardar posiciones iniciales de todos
    if (asientosSeleccionados.includes(asiento.id) && asientosSeleccionados.length > 1) {
      const positions = new Map();
      asientos.forEach(a => {
        if (asientosSeleccionados.includes(a.id)) {
          positions.set(a.id, { x: a.x, y: a.y });
        }
      });
      setDragStartPositions(positions);
    } else {
      // Guardar solo la posición del asiento actual
      const positions = new Map();
      positions.set(asiento.id, { x: asiento.x, y: asiento.y });
      setDragStartPositions(positions);
    }
  };

  // Manejar fin de arrastre de asientos
  const handleAsientoDragEnd = (asientoActualizado, asientoOriginal) => {
    // Si el asiento arrastrado está en la selección múltiple
    if (asientosSeleccionados.includes(asientoOriginal.id) && asientosSeleccionados.length > 1) {
      // Obtener posición inicial del asiento arrastrado
      const startPos = dragStartPositions.get(asientoOriginal.id);
      if (!startPos) {
        // Fallback: mover solo el asiento arrastrado
        setAsientos(asientos.map(a =>
          a.id === asientoActualizado.id ? asientoActualizado : a
        ));
        setDragStartPositions(new Map());
        return;
      }

      // Calcular el delta basado en las posiciones iniciales
      const deltaX = asientoActualizado.x - startPos.x;
      const deltaY = asientoActualizado.y - startPos.y;

      // Actualizar todos los asientos seleccionados con snap to grid
      const nuevosAsientos = asientos.map(a => {
        if (asientosSeleccionados.includes(a.id)) {
          const originalPos = dragStartPositions.get(a.id);
          if (originalPos) {
            const newX = Math.round((originalPos.x + deltaX) / GRID_SIZE) * GRID_SIZE;
            const newY = Math.round((originalPos.y + deltaY) / GRID_SIZE) * GRID_SIZE;
            return {
              ...a,
              x: newX,
              y: newY
            };
          }
        }
        return a;
      });
      setAsientos(nuevosAsientos);
    } else {
      // Movimiento individual
      setAsientos(asientos.map(a =>
        a.id === asientoActualizado.id ? asientoActualizado : a
      ));
    }

    // Limpiar posiciones iniciales
    setDragStartPositions(new Map());
  };

  // Manejar arrastre de elementos especiales
  const handleElementoEspecialDragEnd = (elementoActualizado) => {
    setElementosEspeciales(elementosEspeciales.map(e =>
      e.id === elementoActualizado.id ? elementoActualizado : e
    ));
  };

  // Manejar arrastre del escenario
  const handleEscenarioDragEnd = (escenarioActualizado) => {
    setEscenario(escenarioActualizado);
  };

  // Manejar selección de asientos (con Ctrl/Shift para múltiple)
  const handleAsientoSelect = (asiento, e) => {
    // Si está en modo eliminar, eliminar el asiento
    if (herramientaActiva === "eliminar_asiento") {
      eliminarAsientoEnClick(asiento.id);
      return;
    }

    const isCtrlPressed = e.evt?.ctrlKey || e.evt?.metaKey;
    const isShiftPressed = e.evt?.shiftKey;

    if (isCtrlPressed || isShiftPressed) {
      // Agregar/quitar de selección múltiple
      if (asientosSeleccionados.includes(asiento.id)) {
        setAsientosSeleccionados(asientosSeleccionados.filter(id => id !== asiento.id));
      } else {
        setAsientosSeleccionados([...asientosSeleccionados, asiento.id]);
      }
      setElementoSeleccionado(null);
      setTipoSeleccionado(null);
    } else {
      // Selección simple
      setElementoSeleccionado(asiento);
      setTipoSeleccionado('asiento');
      setAsientosSeleccionados([]);
    }
  };

  // Manejar doble click en asiento para editar
  const handleAsientoDoubleClick = (asiento) => {
    if (herramientaActiva === "seleccionar") {
      abrirModalEditarEntrada(asiento);
    }
  };

  // Organizar asientos seleccionados según configuración
  const organizarAsientosSeleccionados = () => {
    if (asientosSeleccionados.length === 0) {
      Alerts({
        msg: "Error",
        title: "No hay asientos seleccionados",
        code: "400",
        type: "error",
      });
      return;
    }

    const asientosAOrganizar = asientos.filter(a => asientosSeleccionados.includes(a.id));
    const { forma, espaciado, radio, angulo } = configOrganizar;

    // Calcular el centro de los asientos seleccionados
    const centerX = asientosAOrganizar.reduce((sum, a) => sum + a.x, 0) / asientosAOrganizar.length;
    const centerY = asientosAOrganizar.reduce((sum, a) => sum + a.y, 0) / asientosAOrganizar.length;

    let nuevosAsientos = [...asientos];

    if (forma === "linea") {
      // Organizar en línea horizontal
      asientosAOrganizar.forEach((asiento, index) => {
        const asientoIdx = asientos.findIndex(a => a.id === asiento.id);
        nuevosAsientos[asientoIdx] = {
          ...asiento,
          x: Math.round((centerX - (asientosAOrganizar.length * espaciado) / 2 + index * espaciado) / GRID_SIZE) * GRID_SIZE,
          y: Math.round(centerY / GRID_SIZE) * GRID_SIZE
        };
      });
    } else if (forma === "arco") {
      // Organizar en arco
      const anguloInicio = (180 - angulo) / 2;
      const anguloIncremento = angulo / (asientosAOrganizar.length - 1 || 1);

      asientosAOrganizar.forEach((asiento, index) => {
        const anguloActual = (anguloInicio + index * anguloIncremento) * (Math.PI / 180);
        const x = centerX + radio * Math.cos(anguloActual);
        const y = centerY - radio * Math.sin(anguloActual);

        const asientoIdx = asientos.findIndex(a => a.id === asiento.id);
        nuevosAsientos[asientoIdx] = {
          ...asiento,
          x: Math.round(x / GRID_SIZE) * GRID_SIZE,
          y: Math.round(y / GRID_SIZE) * GRID_SIZE
        };
      });
    } else if (forma === "circulo") {
      // Organizar en círculo
      const anguloIncremento = (2 * Math.PI) / asientosAOrganizar.length;

      asientosAOrganizar.forEach((asiento, index) => {
        const anguloActual = index * anguloIncremento;
        const x = centerX + radio * Math.cos(anguloActual);
        const y = centerY + radio * Math.sin(anguloActual);

        const asientoIdx = asientos.findIndex(a => a.id === asiento.id);
        nuevosAsientos[asientoIdx] = {
          ...asiento,
          x: Math.round(x / GRID_SIZE) * GRID_SIZE,
          y: Math.round(y / GRID_SIZE) * GRID_SIZE
        };
      });
    } else if (forma === "grid") {
      // Organizar en cuadrícula
      const asientosPorFila = Math.ceil(Math.sqrt(asientosAOrganizar.length));

      asientosAOrganizar.forEach((asiento, index) => {
        const fila = Math.floor(index / asientosPorFila);
        const columna = index % asientosPorFila;

        const x = centerX - (asientosPorFila * espaciado) / 2 + columna * espaciado;
        const y = centerY - (Math.ceil(asientosAOrganizar.length / asientosPorFila) * espaciado) / 2 + fila * espaciado;

        const asientoIdx = asientos.findIndex(a => a.id === asiento.id);
        nuevosAsientos[asientoIdx] = {
          ...asiento,
          x: Math.round(x / GRID_SIZE) * GRID_SIZE,
          y: Math.round(y / GRID_SIZE) * GRID_SIZE
        };
      });
    }

    setAsientos(nuevosAsientos);
    setMostrarModalOrganizar(false);
  };

  // Seleccionar todos los asientos de un sector
  const seleccionarAsientosDeSector = (sectorId) => {
    const asientosDelSector = asientos.filter(a => a.sector_id === sectorId).map(a => a.id);
    setAsientosSeleccionados(asientosDelSector);
  };

  // Deseleccionar todos
  const deseleccionarTodos = () => {
    setAsientosSeleccionados([]);
    setElementoSeleccionado(null);
    setTipoSeleccionado(null);
  };

  // Alinear asientos seleccionados a la cuadrícula
  const alinearAsientosACuadricula = () => {
    if (asientosSeleccionados.length === 0) return;

    const nuevosAsientos = asientos.map(a => {
      if (asientosSeleccionados.includes(a.id)) {
        return {
          ...a,
          x: Math.round(a.x / GRID_SIZE) * GRID_SIZE,
          y: Math.round(a.y / GRID_SIZE) * GRID_SIZE,
        };
      }
      return a;
    });

    setAsientos(nuevosAsientos);
  };

  // Agregar sector
  const agregarSector = () => {
    if (!formSector.nombre) {
      Alerts({
        msg: "Error",
        title: "El nombre del sector es requerido",
        code: "400",
        type: "error",
      });
      return;
    }

    // Validar que tenga fecha de habilitación si se va a usar en eventos
    if (!formSector.habilitacion_fecha) {
      Alerts({
        msg: "Advertencia",
        title: "Se recomienda agregar una fecha de inicio de venta para este sector",
        code: "400",
        type: "warning",
      });
    }

    // Determinar tipo automáticamente según precio
    const tipoAutomatico = formSector.precio > 0 ? "Venta" : "Gratis";

    const nuevoSector = {
      id: sectorEditando ? sectorEditando.id : `sector-${Date.now()}`,
      ...formSector,
      tipo: tipoAutomatico,
      capacidad: formSector.stock,
      visible: true
    };

    if (sectorEditando) {
      setSectores(sectores.map(s => s.id === sectorEditando.id ? nuevoSector : s));
    } else {
      setSectores([...sectores, nuevoSector]);
    }

    setMostrarModalSector(false);
    setSectorEditando(null);
    setFormSector({
      nombre: "",
      sector: "",
      tipo: "",
      gratis: false,
      precio: 0,
      stock: 0,
      multiplicador: 1,
      color: "#28a745",
      habilitacion_fecha: "",
      habilitacion_hora: "",
      vencimiento_fecha: "",
      vencimiento_hora: ""
    });
  };

  // Eliminar sector
  const eliminarSector = (sectorId) => {
    if (window.confirm("¿Estás seguro de eliminar este sector y todos sus asientos?")) {
      setSectores(sectores.filter(s => s.id !== sectorId));
      setAsientos(asientos.filter(a => a.sector_id !== sectorId));
    }
  };

  // Agregar elemento especial
  const agregarElementoEspecial = () => {
    if (!formElementoEspecial.etiqueta) {
      Alerts({
        msg: "Error",
        title: "La etiqueta del elemento es requerida",
        code: "400",
        type: "error",
      });
      return;
    }

    const nuevoElemento = {
      id: elementoEspecialEditando ? elementoEspecialEditando.id : `elemento-${Date.now()}`,
      ...formElementoEspecial
    };

    if (elementoEspecialEditando) {
      setElementosEspeciales(elementosEspeciales.map(e =>
        e.id === elementoEspecialEditando.id ? nuevoElemento : e
      ));
    } else {
      setElementosEspeciales([...elementosEspeciales, nuevoElemento]);
    }

    setMostrarModalElementoEspecial(false);
    setElementoEspecialEditando(null);
    setFormElementoEspecial({
      tipo: "escalera",
      etiqueta: "",
      width: 100,
      height: 50,
      x: 100,
      y: 100
    });
  };

  // Eliminar elemento especial
  const eliminarElementoEspecial = (elementoId) => {
    if (window.confirm("¿Estás seguro de eliminar este elemento?")) {
      setElementosEspeciales(elementosEspeciales.filter(e => e.id !== elementoId));
    }
  };

  // Modificar escenario
  const modificarEscenario = () => {
    setMostrarModalEscenario(false);
  };

  // Subir imagen de fondo
  const handleImagenFondo = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagenFondo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        {/* Panel lateral */}
        <div className="col-md-3">
          <div className="card">
            <div className="card-header">
              <h5>Mapa de Teatro</h5>
            </div>
            <div className="card-body">
              {/* Selector de sala */}
              <div className="form-group mb-3">
                <label>Seleccionar Sala</label>
                <select
                  className="form-control"
                  value={salaSeleccionada || ""}
                  onChange={(e) => setSalaSeleccionada(e.target.value)}
                >
                  <option value="">-- Seleccione una sala --</option>
                  {salas.map(sala => (
                    <option key={sala.id} value={sala.id}>
                      {sala.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Nombre del mapa */}
              {salaSeleccionada && !mostrarSeleccionMapa && (
                <div className="form-group mb-3">
                  <label>Nombre del Mapa</label>
                  <input
                    type="text"
                    className="form-control"
                    value={nombreMapa}
                    onChange={(e) => setNombreMapa(e.target.value)}
                    placeholder="Ej: Configuración Principal, Evento Especial..."
                  />
                  <small className="form-text text-muted">
                    Dale un nombre descriptivo para identificar este mapa
                  </small>
                </div>
              )}

              {/* Configuración de Workspace */}
              {salaSeleccionada && !mostrarSeleccionMapa && (
                <div className="form-group mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="mb-0">Tamaño del Espacio de Trabajo</label>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowWorkspaceConfig(!showWorkspaceConfig)}
                    >
                      {showWorkspaceConfig ? '−' : '+'}
                    </button>
                  </div>

                  {showWorkspaceConfig && (
                    <div className="border rounded p-3 bg-light">
                      <small className="text-muted d-block mb-2">
                        Configura cuántas cuadrículas (28px cada una) tendrá tu espacio de trabajo
                      </small>

                      {/* Ancho (Columnas) */}
                      <div className="mb-3">
                        <label className="small mb-1">
                          Ancho (Columnas): <strong>{workspaceGridColumns}</strong>
                          <span className="text-muted"> ({workspaceWidth}px)</span>
                        </label>
                        <input
                          type="range"
                          className="form-range"
                          min="20"
                          max="100"
                          value={workspaceGridColumns}
                          onChange={(e) => setWorkspaceGridColumns(parseInt(e.target.value))}
                        />
                        <input
                          type="number"
                          className="form-control form-control-sm mt-1"
                          min="20"
                          max="100"
                          value={workspaceGridColumns}
                          onChange={(e) => setWorkspaceGridColumns(parseInt(e.target.value) || 20)}
                        />
                      </div>

                      {/* Alto (Filas) */}
                      <div className="mb-3">
                        <label className="small mb-1">
                          Alto (Filas): <strong>{workspaceGridRows}</strong>
                          <span className="text-muted"> ({workspaceHeight}px)</span>
                        </label>
                        <input
                          type="range"
                          className="form-range"
                          min="20"
                          max="80"
                          value={workspaceGridRows}
                          onChange={(e) => setWorkspaceGridRows(parseInt(e.target.value))}
                        />
                        <input
                          type="number"
                          className="form-control form-control-sm mt-1"
                          min="20"
                          max="80"
                          value={workspaceGridRows}
                          onChange={(e) => setWorkspaceGridRows(parseInt(e.target.value) || 20)}
                        />
                      </div>

                      {/* Presets */}
                      <div className="mb-2">
                        <small className="text-muted d-block mb-1">Presets rápidos:</small>
                        <div className="btn-group w-100" role="group">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setWorkspaceGridColumns(40);
                              setWorkspaceGridRows(25);
                            }}
                          >
                            Pequeño
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setWorkspaceGridColumns(57);
                              setWorkspaceGridRows(36);
                            }}
                          >
                            Mediano
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setWorkspaceGridColumns(70);
                              setWorkspaceGridRows(50);
                            }}
                          >
                            Grande
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setWorkspaceGridColumns(90);
                              setWorkspaceGridRows(70);
                            }}
                          >
                            Extra Grande
                          </button>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="alert alert-info mb-0 mt-2 py-2 px-2">
                        <small>
                          📐 Dimensiones totales: <strong>{workspaceWidth} × {workspaceHeight}px</strong>
                          <br />
                          🎯 Esta configuración se guarda con el mapa y se usa en la vista previa
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Indicador de mapa actual */}
              {salaSeleccionada && !mostrarSeleccionMapa && (
                <div className="alert alert-success mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>🗺️ Mapa actual:</strong>
                      <br />
                      <small>
                        {mapaSeleccionadoVista ? (
                          <>
                            Mapa #{mapaSeleccionadoVista.id}
                            <br />
                            📍 {asientos.length} asientos | 🎫 {sectores.length} sectores
                          </>
                        ) : (
                          'Nuevo mapa (sin guardar)'
                        )}
                      </small>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        if (mapasDisponibles.length > 0) {
                          setMostrarSeleccionMapa(true);
                        } else {
                          cargarMapasDisponibles(salaSeleccionada);
                        }
                      }}
                      title="Cambiar de mapa"
                    >
                      🔄
                    </button>
                  </div>
                </div>
              )}

              {/* Barra de Herramientas */}
              <div className="form-group mb-3">
                <label className="mb-2">Herramientas</label>
                <div className="d-flex flex-wrap gap-2" style={{ gap: '8px' }}>
                  <button
                    className={`btn ${herramientaActiva === "seleccionar" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setHerramientaActiva("seleccionar")}
                    title="Seleccionar (V)"
                    style={{ flex: '1 1 calc(50% - 4px)', minWidth: '100px' }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <span style={{ fontSize: '20px' }}>↖️</span>
                      <small>Seleccionar</small>
                    </div>
                  </button>
                  <button
                    className={`btn ${herramientaActiva === "mover" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setHerramientaActiva("mover")}
                    title="Mover Vista (H)"
                    style={{ flex: '1 1 calc(50% - 4px)', minWidth: '100px' }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <span style={{ fontSize: '20px' }}>✋</span>
                      <small>Mover</small>
                    </div>
                  </button>
                  <button
                    className={`btn ${herramientaActiva === "agregar_asiento" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => setHerramientaActiva("agregar_asiento")}
                    title="Agregar Asiento (A)"
                    style={{ flex: '1 1 calc(50% - 4px)', minWidth: '100px' }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <span style={{ fontSize: '20px' }}>➕</span>
                      <small>Agregar</small>
                    </div>
                  </button>
                  <button
                    className={`btn ${herramientaActiva === "eliminar_asiento" ? "btn-danger" : "btn-outline-danger"}`}
                    onClick={() => setHerramientaActiva("eliminar_asiento")}
                    title="Eliminar Asiento (D)"
                    style={{ flex: '1 1 calc(50% - 4px)', minWidth: '100px' }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <span style={{ fontSize: '20px' }}>🗑️</span>
                      <small>Eliminar</small>
                    </div>
                  </button>
                </div>
                <small className="form-text text-muted mt-2 d-block">
                  {herramientaActiva === "agregar_asiento"
                    ? "🖱️ Click en el canvas para agregar asientos"
                    : herramientaActiva === "eliminar_asiento"
                      ? "🗑️ Click en un asiento para eliminarlo"
                      : herramientaActiva === "mover"
                        ? "✋ Arrastra el canvas para mover la vista"
                        : "↖️ Click para seleccionar, Ctrl/Shift para múltiple"}
                </small>
              </div>

              {/* Selector de sector para pintar asientos */}
              {herramientaActiva === "agregar_asiento" && (
                <div className="form-group mb-3">
                  <label>Sector a Pintar</label>
                  <select
                    className="form-control"
                    value={sectorActivoPintar || ""}
                    onChange={(e) => setSectorActivoPintar(e.target.value)}
                  >
                    <option value="">-- Selecciona un sector --</option>
                    {sectores.map(sector => {
                      const asientosCreados = asientos.filter(a => a.sector_id === sector.id).length;
                      const stockTotal = sector.capacidad || sector.stock || 0;
                      const disponibles = stockTotal - asientosCreados;
                      return (
                        <option key={sector.id} value={sector.id}>
                          {sector.nombre} ({asientosCreados}/{stockTotal} - {disponibles} disponibles)
                        </option>
                      );
                    })}
                  </select>
                  {sectorActivoPintar && (() => {
                    const sector = sectores.find(s => s.id === sectorActivoPintar);
                    const asientosCreados = asientos.filter(a => a.sector_id === sectorActivoPintar).length;
                    const stockTotal = sector?.capacidad || sector?.stock || 0;
                    const disponibles = stockTotal - asientosCreados;
                    const porcentaje = stockTotal > 0 ? (asientosCreados / stockTotal) * 100 : 0;

                    return (
                      <div className={`alert ${disponibles > 0 ? 'alert-info' : 'alert-warning'} mt-2 mb-0`}>
                        <small>
                          <strong>🎯 Modo pintar activo</strong><br />
                          Sector: <strong>{sector?.nombre}</strong><br />
                          <div className="mt-2">
                            <div className="d-flex justify-content-between mb-1">
                              <span>Asientos: {asientosCreados}/{stockTotal}</span>
                              <span>{disponibles} disponibles</span>
                            </div>
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className={`progress-bar ${porcentaje >= 100 ? 'bg-danger' : porcentaje >= 80 ? 'bg-warning' : 'bg-success'}`}
                                style={{ width: `${Math.min(porcentaje, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                          {disponibles === 0 && (
                            <div className="mt-2 text-danger">
                              ⚠️ <strong>Stock completo</strong> - No puedes agregar más asientos
                            </div>
                          )}
                        </small>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Zoom */}
              <div className="form-group mb-3">
                <label>Zoom: {Math.round(zoom * 100)}%</label>
                <input
                  type="range"
                  className="form-range"
                  min="0.1"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                />
              </div>

              {/* Sectores */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label>Sectores</label>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setMostrarModalSector(true);
                      setSectorEditando(null);
                    }}
                  >
                    + Nuevo
                  </button>
                </div>
                <div className="list-group" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {sectores.map(sector => {
                    const asientosCreados = asientos.filter(a => a.sector_id === sector.id).length;
                    const stockTotal = sector.capacidad || sector.stock || 0;
                    const disponibles = stockTotal - asientosCreados;
                    const porcentaje = stockTotal > 0 ? (asientosCreados / stockTotal) * 100 : 0;

                    return (
                      <div key={sector.id} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <div style={{ flex: 1 }}>
                            <div className="d-flex align-items-center mb-1">
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: sector.color,
                                  borderRadius: '50%',
                                  marginRight: '8px'
                                }}
                              ></span>
                              <strong>{sector.nombre}</strong>
                            </div>
                            <small className="d-block">
                              <strong>Asientos:</strong> {asientosCreados}/{stockTotal}
                              {disponibles === 0 && <span className="text-danger ms-1">• Completo</span>}
                            </small>
                            <div className="progress mt-1" style={{ height: '4px' }}>
                              <div
                                className={`progress-bar ${porcentaje >= 100 ? 'bg-danger' : porcentaje >= 80 ? 'bg-warning' : 'bg-success'}`}
                                style={{ width: `${Math.min(porcentaje, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                          <div>
                            <button
                              className="btn btn-sm btn-success me-1"
                              onClick={() => generarAsientosParaSector(sector.id)}
                              title="Generar asientos"
                            >
                              ⚡
                            </button>
                            <button
                              className="btn btn-sm btn-warning me-1"
                              onClick={() => {
                                setSectorEditando(sector);
                                setFormSector(sector);
                                setMostrarModalSector(true);
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              className="btn btn-sm btn-danger me-1"
                              onClick={() => eliminarSector(sector.id)}
                            >
                              🗑️
                            </button>
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => seleccionarAsientosDeSector(sector.id)}
                              title="Seleccionar asientos del sector"
                            >
                              ☑️
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Selección individual */}
              {elementoSeleccionado && tipoSeleccionado === 'asiento' && (
                <div className="mb-3">
                  <div className="alert alert-primary">
                    <strong>Asiento seleccionado</strong>
                    <div className="mt-2">
                      <small>
                        <strong>Número:</strong> {elementoSeleccionado.numero}<br />
                        <strong>Nombre:</strong> {elementoSeleccionado.nombre || 'Sin nombre'}<br />
                        <strong>Precio:</strong> ${elementoSeleccionado.precio || 0}<br />
                        <strong>Tipo:</strong> {elementoSeleccionado.tipo_entrada || (elementoSeleccionado.precio > 0 ? 'Venta' : 'Gratis')}
                      </small>
                    </div>
                    <div className="mt-2">
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => abrirModalEditarEntrada(elementoSeleccionado)}
                      >
                        ✏️ Editar Entrada
                      </button>
                    </div>
                    <small className="d-block mt-2 text-muted">
                      💡 Doble click en un asiento para editarlo
                    </small>
                  </div>
                </div>
              )}

              {/* Selección Múltiple */}
              {asientosSeleccionados.length > 0 && (
                <div className="mb-3">
                  <div className="alert alert-success">
                    <strong>{asientosSeleccionados.length} asientos seleccionados</strong>
                    <div className="mt-2">
                      <button
                        className="btn btn-sm btn-primary me-1"
                        onClick={() => setMostrarModalOrganizar(true)}
                      >
                        📐 Organizar
                      </button>
                      <button
                        className="btn btn-sm btn-info me-1"
                        onClick={alinearAsientosACuadricula}
                        title="Alinear a cuadrícula"
                      >
                        🧲 Alinear
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={deseleccionarTodos}
                      >
                        ✖️ Deseleccionar
                      </button>
                    </div>
                    <small className="d-block mt-2 text-muted">
                      <strong>Selección múltiple:</strong><br />
                      • Ctrl/Shift + Click en asiento<br />
                      • Ctrl/Shift + Arrastrar en canvas<br />
                      • Herramienta "Seleccionar" + Arrastrar
                    </small>
                  </div>
                </div>
              )}

              {/* Escenario */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label>Escenario</label>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setMostrarModalEscenario(true)}
                  >
                    ⚙️ Configurar
                  </button>
                </div>
              </div>

              {/* Elementos Especiales */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label>Elementos Especiales</label>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setMostrarModalElementoEspecial(true);
                      setElementoEspecialEditando(null);
                    }}
                  >
                    + Nuevo
                  </button>
                </div>
                <div className="list-group" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {elementosEspeciales.map(elemento => (
                    <div key={elemento.id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{elemento.etiqueta}</strong>
                          <br />
                          <small>{elemento.tipo}</small>
                        </div>
                        <div>
                          <button
                            className="btn btn-sm btn-warning me-1"
                            onClick={() => {
                              setElementoEspecialEditando(elemento);
                              setFormElementoEspecial(elemento);
                              setMostrarModalElementoEspecial(true);
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => eliminarElementoEspecial(elemento.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Imagen de fondo */}
              <div className="form-group mb-3">
                <label>Imagen de Fondo</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImagenFondo}
                />
                {imagenFondo && (
                  <div className="mt-2">
                    <label>Opacidad: {Math.round(opacidadFondo * 100)}%</label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={opacidadFondo}
                      onChange={(e) => setOpacidadFondo(parseFloat(e.target.value))}
                    />
                  </div>
                )}
              </div>

              {/* Botón guardar */}
              <button
                className="btn btn-primary w-100"
                onClick={guardarMapa}
                disabled={!salaSeleccionada}
              >
                💾 Guardar Mapa
              </button>
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="col-md-9">
          {!salaSeleccionada || mostrarSeleccionMapa ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <div style={{ fontSize: '80px', opacity: 0.3 }}>🎭</div>
                <h4 className="text-muted mt-3">
                  {!salaSeleccionada
                    ? 'Selecciona una sala para comenzar'
                    : 'Selecciona o crea un mapa'
                  }
                </h4>
                <p className="text-muted">
                  {!salaSeleccionada
                    ? 'Usa el selector de la izquierda para elegir una sala'
                    : 'Elige un mapa existente o crea uno nuevo desde el modal'
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body p-0">
                <Stage
                  ref={stageRef}
                  width={workspaceWidth}
                  height={workspaceHeight}
                  scaleX={zoom}
                  scaleY={zoom}
                  x={stagePos.x}
                  y={stagePos.y}
                  onWheel={handleWheel}
                  onMouseDown={handleStageMouseDown}
                  onMouseMove={handleStageMouseMove}
                  onMouseUp={handleStageMouseUp}
                  draggable={herramientaActiva === "mover"}
                  style={{
                    border: '1px solid #ddd',
                    cursor: herramientaActiva === "agregar_asiento"
                      ? 'crosshair'
                      : herramientaActiva === "eliminar_asiento"
                        ? 'not-allowed'
                        : 'default'
                  }}
                >
                  <Layer>
                    {/* Fondo blanco */}
                    <Rect
                      x={0}
                      y={0}
                      width={5000}
                      height={5000}
                      fill="#FFFFFF"
                    />

                    {/* Grid */}
                    {Array.from({ length: Math.floor(5000 / GRID_SIZE) }).map((_, i) => {
                      // Líneas más gruesas cada 4 cuadrículas (cada 112px)
                      const isMainGrid = i % 4 === 0;
                      return (
                        <React.Fragment key={`grid-${i}`}>
                          <Line
                            points={[i * GRID_SIZE, 0, i * GRID_SIZE, 5000]}
                            stroke={isMainGrid ? "#D0D0D0" : "#E8E8E8"}
                            strokeWidth={isMainGrid ? 1 : 0.5}
                            listening={false}
                          />
                          <Line
                            points={[0, i * GRID_SIZE, 5000, i * GRID_SIZE]}
                            stroke={isMainGrid ? "#D0D0D0" : "#E8E8E8"}
                            strokeWidth={isMainGrid ? 1 : 0.5}
                            listening={false}
                          />
                        </React.Fragment>
                      );
                    })}

                    {/* Imagen de fondo */}
                    {imagenFondo && (
                      <BackgroundImage
                        imagenFondo={imagenFondo}
                        opacidad={opacidadFondo}
                        width={workspaceWidth}
                        height={workspaceHeight}
                      />
                    )}

                    {/* Escenario */}
                    <Escenario
                      escenario={escenario}
                      isSelected={tipoSeleccionado === 'escenario'}
                      onSelect={() => {
                        setElementoSeleccionado(escenario);
                        setTipoSeleccionado('escenario');
                      }}
                      onDragEnd={handleEscenarioDragEnd}
                    />

                    {/* Asientos */}
                    {asientos.map(asiento => {
                      const sector = sectores.find(s => s.id == asiento.sector_id);
                      return (
                        <Asiento
                          key={asiento.id}
                          asiento={asiento}
                          sector={sector}
                          isSelected={elementoSeleccionado?.id === asiento.id && tipoSeleccionado === 'asiento'}
                          isMultiSelected={asientosSeleccionados.includes(asiento.id)}
                          onSelect={(e) => handleAsientoSelect(asiento, e)}
                          onDragStart={handleAsientoDragStart}
                          onDragEnd={handleAsientoDragEnd}
                          onDoubleClick={handleAsientoDoubleClick}
                          herramientaActiva={herramientaActiva}
                        />
                      );
                    })}

                    {/* Elementos especiales */}
                    {elementosEspeciales.map(elemento => (
                      <ElementoEspecial
                        key={elemento.id}
                        elemento={elemento}
                        isSelected={elementoSeleccionado?.id === elemento.id && tipoSeleccionado === 'especial'}
                        onSelect={() => {
                          setElementoSeleccionado(elemento);
                          setTipoSeleccionado('especial');
                        }}
                        onDragEnd={handleElementoEspecialDragEnd}
                      />
                    ))}

                    {/* Rectángulo de selección */}
                    {isSelecting && selectionRect.width > 5 && selectionRect.height > 5 && (
                      <Rect
                        x={selectionRect.x}
                        y={selectionRect.y}
                        width={selectionRect.width}
                        height={selectionRect.height}
                        fill="rgba(0, 123, 255, 0.1)"
                        stroke="#007bff"
                        strokeWidth={2 / zoom}
                        dash={[5 / zoom, 5 / zoom]}
                        listening={false}
                      />
                    )}
                  </Layer>
                </Stage>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal para agregar/editar sector */}
      {mostrarModalSector && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {sectorEditando ? 'Editar Sector' : 'Nuevo Sector'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setMostrarModalSector(false);
                    setSectorEditando(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Nombre del Sector</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formSector.nombre}
                      onChange={(e) => setFormSector({ ...formSector, nombre: e.target.value })}
                      placeholder="Ej: Platea Alta"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Sector (Categoría)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formSector.sector}
                      onChange={(e) => setFormSector({ ...formSector, sector: e.target.value })}
                      placeholder="Ej: VIP, General, etc."
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label>Precio</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formSector.precio}
                      onChange={(e) => setFormSector({ ...formSector, precio: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                      min="0"
                    />
                    <small className="form-text text-muted">
                      Tipo: {formSector.precio > 0 ? '✅ Venta' : '🎁 Gratis'}
                    </small>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label>Stock (Capacidad)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formSector.stock}
                      onChange={(e) => setFormSector({ ...formSector, stock: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label>Color</label>
                    <input
                      type="color"
                      className="form-control"
                      value={formSector.color}
                      onChange={(e) => setFormSector({ ...formSector, color: e.target.value })}
                      style={{ height: '38px' }}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="border-bottom pb-2">Fechas de Habilitación</h6>
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Estas fechas se usarán como valores por defecto cuando el mapa se asigne a un evento
                  </small>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Fecha de Habilitación <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={formSector.habilitacion_fecha}
                      onChange={(e) => setFormSector({ ...formSector, habilitacion_fecha: e.target.value })}
                    />
                    <small className="text-muted">Fecha de inicio de venta de este sector</small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Hora de Habilitación</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formSector.habilitacion_hora}
                      onChange={(e) => setFormSector({ ...formSector, habilitacion_hora: e.target.value })}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="border-bottom pb-2">Fechas de Vencimiento (Opcional)</h6>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Fecha de Vencimiento</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formSector.vencimiento_fecha}
                      onChange={(e) => setFormSector({ ...formSector, vencimiento_fecha: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Hora de Vencimiento</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formSector.vencimiento_hora}
                      onChange={(e) => setFormSector({ ...formSector, vencimiento_hora: e.target.value })}
                    />
                  </div>
                </div>

                <div className="alert alert-info">
                  <strong>ℹ️ Información:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Tipo:</strong> Se determina automáticamente según el precio (Venta si {'>'} 0, Gratis si = 0)</li>
                    <li><strong>Todas las entradas del sector:</strong> Heredarán estos valores por defecto</li>
                    <li><strong>Personalización:</strong> Cada asiento puede editarse individualmente después</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setMostrarModalSector(false);
                    setSectorEditando(null);
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={agregarSector}
                >
                  {sectorEditando ? 'Actualizar' : 'Agregar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar/editar elemento especial */}
      {mostrarModalElementoEspecial && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {elementoEspecialEditando ? 'Editar Elemento' : 'Nuevo Elemento Especial'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setMostrarModalElementoEspecial(false);
                    setElementoEspecialEditando(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label>Tipo</label>
                  <select
                    className="form-control"
                    value={formElementoEspecial.tipo}
                    onChange={(e) => setFormElementoEspecial({ ...formElementoEspecial, tipo: e.target.value })}
                  >
                    <option value="escalera">Escalera</option>
                    <option value="ingreso">Ingreso</option>
                    <option value="salida_emergencia">Salida de Emergencia</option>
                    <option value="baño">Baño</option>
                    <option value="pasillo">Pasillo</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label>Etiqueta</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formElementoEspecial.etiqueta}
                    onChange={(e) => setFormElementoEspecial({ ...formElementoEspecial, etiqueta: e.target.value })}
                    placeholder="Ej: Escalera Principal"
                  />
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Ancho</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formElementoEspecial.width}
                      onChange={(e) => setFormElementoEspecial({ ...formElementoEspecial, width: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Alto</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formElementoEspecial.height}
                      onChange={(e) => setFormElementoEspecial({ ...formElementoEspecial, height: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Posición X</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formElementoEspecial.x}
                      onChange={(e) => setFormElementoEspecial({ ...formElementoEspecial, x: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Posición Y</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formElementoEspecial.y}
                      onChange={(e) => setFormElementoEspecial({ ...formElementoEspecial, y: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setMostrarModalElementoEspecial(false);
                    setElementoEspecialEditando(null);
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={agregarElementoEspecial}
                >
                  {elementoEspecialEditando ? 'Actualizar' : 'Agregar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para configurar escenario */}
      {mostrarModalEscenario && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configurar Escenario</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setMostrarModalEscenario(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label>Tipo de Escenario</label>
                  <select
                    className="form-control"
                    value={escenario.tipo}
                    onChange={(e) => setEscenario({ ...escenario, tipo: e.target.value })}
                  >
                    <option value="semicircular">Semicircular</option>
                    <option value="rectangular">Rectangular</option>
                  </select>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Ancho</label>
                    <input
                      type="number"
                      className="form-control"
                      value={escenario.width}
                      onChange={(e) => setEscenario({ ...escenario, width: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Alto</label>
                    <input
                      type="number"
                      className="form-control"
                      value={escenario.height}
                      onChange={(e) => setEscenario({ ...escenario, height: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Posición X</label>
                    <input
                      type="number"
                      className="form-control"
                      value={escenario.x}
                      onChange={(e) => setEscenario({ ...escenario, x: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Posición Y</label>
                    <input
                      type="number"
                      className="form-control"
                      value={escenario.y}
                      onChange={(e) => setEscenario({ ...escenario, y: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setMostrarModalEscenario(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={modificarEscenario}
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para configurar generador de asientos */}
      {mostrarModalGenerador && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configurar Generador de Asientos</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setMostrarModalGenerador(false);
                    setSectorGenerando(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <small>
                    <strong>📐 Alineación automática:</strong> Los asientos se alinearán automáticamente a la cuadrícula (28px).
                    Se recomienda usar múltiplos de 28 para el espaciado.
                  </small>
                </div>
                <div className="mb-3">
                  <label>Forma de Disposición</label>
                  <select
                    className="form-control"
                    value={configGenerador.forma}
                    onChange={(e) => setConfigGenerador({ ...configGenerador, forma: e.target.value })}
                  >
                    <option value="cuadrado">Cuadrícula (Rectangular)</option>
                    <option value="semicircular">Semicircular</option>
                    <option value="circular">Circular</option>
                  </select>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Número de Filas</label>
                    <input
                      type="number"
                      className="form-control"
                      value={configGenerador.filas}
                      onChange={(e) => setConfigGenerador({ ...configGenerador, filas: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Asientos por Fila</label>
                    <input
                      type="number"
                      className="form-control"
                      value={configGenerador.asientosPorFila}
                      onChange={(e) => setConfigGenerador({ ...configGenerador, asientosPorFila: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Espacio Horizontal</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={configGenerador.espacioHorizontal}
                        onChange={(e) => setConfigGenerador({ ...configGenerador, espacioHorizontal: parseInt(e.target.value) })}
                      />
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        type="button"
                        onClick={() => setConfigGenerador({
                          ...configGenerador,
                          espacioHorizontal: Math.round(configGenerador.espacioHorizontal / GRID_SIZE) * GRID_SIZE
                        })}
                        title="Ajustar a cuadrícula"
                      >
                        📐
                      </button>
                    </div>
                    <small className="text-muted">Recomendado: 28, 56, 84, 112</small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Espacio Vertical</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={configGenerador.espacioVertical}
                        onChange={(e) => setConfigGenerador({ ...configGenerador, espacioVertical: parseInt(e.target.value) })}
                      />
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        type="button"
                        onClick={() => setConfigGenerador({
                          ...configGenerador,
                          espacioVertical: Math.round(configGenerador.espacioVertical / GRID_SIZE) * GRID_SIZE
                        })}
                        title="Ajustar a cuadrícula"
                      >
                        📐
                      </button>
                    </div>
                    <small className="text-muted">Recomendado: 28, 56, 84, 112</small>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Posición Inicial X</label>
                    <input
                      type="number"
                      className="form-control"
                      value={configGenerador.offsetX}
                      onChange={(e) => setConfigGenerador({ ...configGenerador, offsetX: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Posición Inicial Y</label>
                    <input
                      type="number"
                      className="form-control"
                      value={configGenerador.offsetY}
                      onChange={(e) => setConfigGenerador({ ...configGenerador, offsetY: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setMostrarModalGenerador(false);
                    setSectorGenerando(null);
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={generarAsientosConConfig}
                >
                  Generar Asientos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para organizar asientos seleccionados */}
      {mostrarModalOrganizar && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Organizar Asientos Seleccionados</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setMostrarModalOrganizar(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">
                  {asientosSeleccionados.length} asientos seleccionados
                </p>

                <div className="mb-3">
                  <label>Forma de Organización</label>
                  <select
                    className="form-control"
                    value={configOrganizar.forma}
                    onChange={(e) => setConfigOrganizar({ ...configOrganizar, forma: e.target.value })}
                  >
                    <option value="linea">Línea Horizontal</option>
                    <option value="arco">Arco / Semicírculo</option>
                    <option value="circulo">Círculo Completo</option>
                    <option value="grid">Cuadrícula</option>
                  </select>
                </div>

                {configOrganizar.forma === "linea" && (
                  <div className="mb-3">
                    <label>Espaciado entre Asientos</label>
                    <input
                      type="number"
                      className="form-control"
                      value={configOrganizar.espaciado}
                      onChange={(e) => setConfigOrganizar({ ...configOrganizar, espaciado: parseInt(e.target.value) })}
                    />
                  </div>
                )}

                {configOrganizar.forma === "arco" && (
                  <>
                    <div className="mb-3">
                      <label>Radio del Arco</label>
                      <input
                        type="number"
                        className="form-control"
                        value={configOrganizar.radio}
                        onChange={(e) => setConfigOrganizar({ ...configOrganizar, radio: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="mb-3">
                      <label>Ángulo del Arco (grados)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={configOrganizar.angulo}
                        min="30"
                        max="180"
                        onChange={(e) => setConfigOrganizar({ ...configOrganizar, angulo: parseInt(e.target.value) })}
                      />
                      <small className="form-text text-muted">Entre 30° y 180°</small>
                    </div>
                  </>
                )}

                {configOrganizar.forma === "circulo" && (
                  <div className="mb-3">
                    <label>Radio del Círculo</label>
                    <input
                      type="number"
                      className="form-control"
                      value={configOrganizar.radio}
                      onChange={(e) => setConfigOrganizar({ ...configOrganizar, radio: parseInt(e.target.value) })}
                    />
                  </div>
                )}

                {configOrganizar.forma === "grid" && (
                  <div className="mb-3">
                    <label>Espaciado entre Asientos</label>
                    <input
                      type="number"
                      className="form-control"
                      value={configOrganizar.espaciado}
                      onChange={(e) => setConfigOrganizar({ ...configOrganizar, espaciado: parseInt(e.target.value) })}
                    />
                    <small className="form-text text-muted">
                      Se organizarán en una cuadrícula automática
                    </small>
                  </div>
                )}

                <div className="alert alert-info">
                  <strong>💡 Consejos:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Línea:</strong> Organiza en fila horizontal</li>
                    <li><strong>Arco:</strong> Forma semicircular como un anfiteatro</li>
                    <li><strong>Círculo:</strong> Círculo completo alrededor de un punto</li>
                    <li><strong>Cuadrícula:</strong> Grid rectangular automático</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setMostrarModalOrganizar(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={organizarAsientosSeleccionados}
                >
                  📐 Aplicar Organización
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar entrada/asiento */}
      {mostrarModalEditarEntrada && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar Entrada - Asiento {asientoEditando?.numero}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setMostrarModalEditarEntrada(false);
                    setAsientoEditando(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Nombre de la Entrada</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formEntrada.nombre}
                      onChange={(e) => setFormEntrada({ ...formEntrada, nombre: e.target.value })}
                      placeholder="Ej: Platea Alta - Asiento 1"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Sector</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formEntrada.sector}
                      onChange={(e) => setFormEntrada({ ...formEntrada, sector: e.target.value })}
                      placeholder="Nombre del sector"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Precio</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formEntrada.precio}
                      onChange={(e) => setFormEntrada({ ...formEntrada, precio: e.target.value })}
                      step="0.01"
                      min="0"
                    />
                    <small className="form-text text-muted">
                      El tipo será automático: {formEntrada.precio > 0 ? '✅ Venta' : '🎁 Gratis'}
                    </small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Stock</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formEntrada.stock}
                      onChange={(e) => setFormEntrada({ ...formEntrada, stock: e.target.value })}
                      min="1"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="border-bottom pb-2">Fechas de Habilitación</h6>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Fecha de Habilitación</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formEntrada.habilitacion_fecha}
                      onChange={(e) => setFormEntrada({ ...formEntrada, habilitacion_fecha: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Hora de Habilitación</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formEntrada.habilitacion_hora}
                      onChange={(e) => setFormEntrada({ ...formEntrada, habilitacion_hora: e.target.value })}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="border-bottom pb-2">Fechas de Vencimiento</h6>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Fecha de Vencimiento</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formEntrada.vencimiento_fecha}
                      onChange={(e) => setFormEntrada({ ...formEntrada, vencimiento_fecha: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label>Hora de Vencimiento</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formEntrada.vencimiento_hora}
                      onChange={(e) => setFormEntrada({ ...formEntrada, vencimiento_hora: e.target.value })}
                    />
                  </div>
                </div>

                <div className="alert alert-info">
                  <strong>ℹ️ Información:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Tipo:</strong> Se determina automáticamente según el precio (Venta si precio {'>'} 0, Gratis si precio = 0)</li>
                    <li><strong>Habilitación:</strong> Fecha y hora desde cuando estará disponible la entrada</li>
                    <li><strong>Vencimiento:</strong> Fecha y hora hasta cuando estará disponible la entrada</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setMostrarModalEditarEntrada(false);
                    setAsientoEditando(null);
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={guardarCambiosEntrada}
                >
                  💾 Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para seleccionar mapa existente o crear nuevo */}
      {mostrarSeleccionMapa && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Seleccionar Mapa de Sala</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setMostrarSeleccionMapa(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  <strong>ℹ️ Mapas disponibles para esta sala:</strong> {mapasDisponibles.length}
                </div>

                <div className="row">
                  {/* Card para crear nuevo mapa */}
                  <div className="col-md-4 mb-3">
                    <div
                      className="card h-100 border-primary"
                      style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      onClick={crearMapaNuevo}
                    >
                      <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-4">
                        <div
                          className="mb-3"
                          style={{
                            fontSize: '64px',
                            color: '#007bff',
                            lineHeight: 1
                          }}
                        >
                          ➕
                        </div>
                        <h5 className="card-title text-primary mb-2">Crear Nuevo Mapa</h5>
                        <p className="card-text text-muted">
                          Comenzar desde cero con un mapa vacío
                        </p>
                      </div>
                      <div className="card-footer bg-primary text-white text-center">
                        <small><strong>Click para crear</strong></small>
                      </div>
                    </div>
                  </div>

                  {/* Cards de mapas existentes */}
                  {mapasDisponibles.map((mapa) => {
                    let parsedSiteMap = null;
                    let totalAsientos = 0;
                    let totalSectores = 0;
                    let nombreMapa = mapa.nombre || null;

                    try {
                      parsedSiteMap = typeof mapa.site_map === 'string'
                        ? JSON.parse(mapa.site_map)
                        : mapa.site_map;

                      totalAsientos = parsedSiteMap?.asientos?.length || 0;
                      totalSectores = parsedSiteMap?.sectores?.length || 0;
                      // Si no hay nombre en el modelo, buscar en el JSON
                      if (!nombreMapa && parsedSiteMap?.nombre) {
                        nombreMapa = parsedSiteMap.nombre;
                      }
                    } catch (e) {
                      console.error("Error parseando mapa:", e);
                    }

                    return (
                      <div key={mapa.id} className="col-md-4 mb-3">
                        <div
                          className="card h-100 border-success"
                          style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          onClick={() => cargarMapaEspecifico(mapa)}
                        >
                          <div className="card-header bg-success text-white">
                            <h6 className="mb-0">
                              🗺️ {nombreMapa || `Mapa #${mapa.id}`}
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="mb-2">
                              <strong>ID:</strong>
                              <span className="badge bg-secondary ms-2">#{mapa.id}</span>
                            </div>
                            <div className="mb-2">
                              <strong>📍 Asientos:</strong>
                              <span className="badge bg-primary ms-2">{totalAsientos}</span>
                            </div>
                            <div className="mb-2">
                              <strong>🎫 Sectores:</strong>
                              <span className="badge bg-info ms-2">{totalSectores}</span>
                            </div>
                            <div className="mb-2">
                              <strong>📅 Creado:</strong>
                              <small className="text-muted ms-2">
                                {mapa.created_at
                                  ? new Date(mapa.created_at).toLocaleDateString('es-AR', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                  : 'N/A'
                                }
                              </small>
                            </div>
                            <div>
                              <strong>🔄 Actualizado:</strong>
                              <small className="text-muted ms-2">
                                {mapa.updated_at
                                  ? new Date(mapa.updated_at).toLocaleDateString('es-AR', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                  : 'N/A'
                                }
                              </small>
                            </div>

                            {/* Preview de sectores */}
                            {totalSectores > 0 && parsedSiteMap?.sectores && (
                              <div className="mt-3">
                                <small className="text-muted d-block mb-2">
                                  <strong>Sectores:</strong>
                                </small>
                                <div className="d-flex flex-wrap gap-1" style={{ gap: '4px' }}>
                                  {parsedSiteMap.sectores.slice(0, 5).map((sector, idx) => (
                                    <span
                                      key={idx}
                                      className="badge"
                                      style={{
                                        backgroundColor: sector.color || '#28a745',
                                        color: '#fff',
                                        fontSize: '10px'
                                      }}
                                    >
                                      {sector.nombre}
                                    </span>
                                  ))}
                                  {parsedSiteMap.sectores.length > 5 && (
                                    <span className="badge bg-secondary" style={{ fontSize: '10px' }}>
                                      +{parsedSiteMap.sectores.length - 5}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="card-footer bg-light text-center">
                            <small><strong>Click para cargar</strong></small>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {mapasDisponibles.length === 0 && (
                  <div className="alert alert-warning text-center">
                    <strong>⚠️ No hay mapas disponibles</strong>
                    <p className="mb-0 mt-2">Crea un nuevo mapa para comenzar</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setMostrarSeleccionMapa(false);
                    setSalaSeleccionada(null);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapaTeatroKonva;
